import React from 'react'

/**
 * Coast Terms of Service content rendered with nicer typography,
 * while preserving the EXACT text (including bullet symbols ● and ○).
 *
 * Source: Coast - Terms of Service 1_1_26.pdf :contentReference[oaicite:0]{index=0}
 */

const styles = {
  wrap: {
    color: '#fff',
    lineHeight: 1.65,
    fontSize: '14px',
    letterSpacing: '0.2px',
  } as React.CSSProperties,

  title: {
    fontWeight: 900,
    fontSize: '22px',
    letterSpacing: '0.6px',
    textTransform: 'none',
  } as React.CSSProperties,

  subtitle: {
    marginTop: '6px',
    fontWeight: 700,
    fontSize: '13px',
    opacity: 0.95,
  } as React.CSSProperties,

  para: {
    marginTop: '12px',
    marginBottom: 0,
    whiteSpace: 'pre-wrap',
    opacity: 0.97,
  } as React.CSSProperties,

  section: {
    marginTop: '18px',
    fontWeight: 900,
    fontSize: '16px',
    letterSpacing: '0.3px',
  } as React.CSSProperties,

  smallLead: {
    marginTop: '10px',
    marginBottom: '6px',
    whiteSpace: 'pre-wrap',
    fontWeight: 600,
    opacity: 0.95,
  } as React.CSSProperties,

  divider: {
    marginTop: '14px',
    marginBottom: '14px',
    border: 0,
    height: '1px',
    background: 'rgba(255,255,255,0.15)',
  } as React.CSSProperties,

  list: (indent: number) =>
    ({
      listStyle: 'none',
      paddingLeft: indent,
      margin: 0,
      marginTop: '6px',
    } as React.CSSProperties),

  li: {
    margin: '7px 0',
    whiteSpace: 'pre-wrap',
    opacity: 0.97,
  } as React.CSSProperties,

  allCapsBlock: {
    marginTop: '10px',
    whiteSpace: 'pre-wrap',
    fontWeight: 900,
    fontSize: '12.5px',
    letterSpacing: '0.4px',
    opacity: 0.98,
  } as React.CSSProperties,
}

const BulletList: React.FC<{ items: string[]; indent?: number }> = ({ items, indent = 0 }) => (
  <ul style={styles.list(indent)}>
    {items.map((t, i) => (
      <li key={i} style={styles.li}>
        {t}
      </li>
    ))}
  </ul>
)

export const ToSContent: React.FC<{ popup: boolean }> = ({ popup }) => {
  const wrapStyle: React.CSSProperties = popup
    ? styles.wrap // your existing popup style (white text)
    : {
      ...styles.wrap,
      color: 'inherit', // <-- take from page/theme
      opacity: 1, // optional: remove opacity dimming
    }

  return (
    <div style={wrapStyle}>
      <div style={styles.title}>Coast Terms of Service</div>
      <div style={styles.subtitle}>Effective Date: January 1, 2026</div>

      <hr style={styles.divider} />

      <p style={styles.para}>
        Welcome to Coast. These Terms of Service ("Terms") constitute a legally binding software rental agreement
        ("Agreement") between you ("User," "you," or "your") and Coast Software (Nevada), the parent company providing
        the Coast software platform and related services (collectively, the "Services"). By accessing, downloading,
        installing, or using the Services in any manner, you agree to be bound by these Terms. If you do not agree to
        these Terms, you must not access or use the Services.
      </p>

      <p style={styles.para}>
        The Services are owned and operated by Coast Software (Nevada) ("we," "us," or "our"). We reserve the right to
        modify these Terms at any time. We will notify you of material changes by posting the updated Terms on our
        website at 0xcoast.com or through the Services at least 30 days in advance, unless the changes are required by
        law to take effect sooner. Your continued use of the Services after such changes constitutes your acceptance of
        the revised Terms. If you do not agree to the changes, you must stop using the Services immediately.
      </p>

      <p style={styles.para}>
        This Agreement is strictly for the rental of software and private keys, as detailed below. It does not involve
        any financial services, asset custody, or fiat onramp activities. Participation in any optional features, such
        as the Game of Skill or Coast Lock & Earn, requires acceptance of separate agreements or acknowledgments as
        described in Sections 10 and 6. The Services operate on the PulseChain blockchain and are designed for utility
        purposes only, without any expectation of profits or financial returns. By using the Services, you acknowledge
        and represent that your participation is for protocol utility purposes only, with no intent to transmit value,
        and you assume all regulatory compliance responsibilities. You further acknowledge that any bridged assets on
        PulseChain (e.g., copies of stablecoins like pUSDC or pUSDT) are not redeemable for fiat currency or original
        Ethereum assets, lack equivalent value in real currency, and may become illiquid or valueless if the PulseChain
        Bridge fails or is abandoned.
      </p>

      <div style={styles.section}>1. Description of Services</div>

      <p style={styles.para}>
        The Services consist of a software platform provided on a rental basis, enabling you to temporarily rent the use
        of private keys for the purpose of executing your own on-chain transactions on blockchain networks, including
        user-initiated swaps of bridged assets on PulseChain. This is exclusively a software and private key rental
        arrangement, similar to licensing software for temporary use. We grant you a limited, non-exclusive,
        non-transferable, revocable license to access and use the software and rented private keys solely for the
        purposes described herein, subject to payment of applicable rental fees and compliance with these Terms.
      </p>

      <p style={styles.smallLead}>Under this rental Agreement:</p>

      <BulletList
        items={[
          '● Your Responsibility for Transactions: You are solely responsible for initiating,\nauthorizing, and executing all transactions using the rented private keys, including any\nswaps of bridged assets. We provide access to the private keys on a rental basis only,\nand you retain full control over any on-chain actions performed with them. We do not\nexecute, approve, or monitor your transactions.',
          '● No Fiat Onramp Services: We do not provide any fiat onramp services. The Services\ndo not facilitate the conversion, deposit, or withdrawal of fiat currencies (e.g., USD, EUR)\ninto or from cryptocurrencies or digital assets. Any fiat-related activities must be handled\nentirely through third-party providers outside of our Services.',
          '● No Control Over Transaction Elements: We exercise no control or influence over\ncritical aspects of your transactions, including but not limited to:',
        ]}
      />

      <BulletList
        indent={20}
        items={[
          '○ Liquidity provision, sourcing, or management.',
          '○ Price feeds, market data, or oracle services.',
          '○ Transaction settlement, confirmation, or finality on the blockchain.',
          '○ Custody, storage, or control of any digital assets, funds, or blockchain elements,\nexcept for the temporary rental of the private key itself.',
        ]}
      />

      <BulletList
        items={[
          '● Scope of Rental: The rental is limited to providing you with temporary access to the\nsoftware interface and private keys. Once rented, you are authorized to use the private\nkeys for your independent on-chain transactions, including swaps of bridged assets. We\nhave no involvement in, liability for, or oversight of the outcomes, successes, failures, or\nrisks associated with those transactions. You acknowledge that bridged assets on\nPulseChain are copies without official issuer support, are not redeemable for fiat or\noriginal assets, and do not constitute convertible virtual currency or value substitutes for\nmoney transmission purposes.',
        ]}
      />

      <p style={styles.para}>
        By renting and using the Services, you expressly acknowledge and accept that all inherent risks of blockchain
        technology and transactions—such as market volatility, smart contract vulnerabilities, network delays, hacks,
        regulatory changes, and potential financial losses—are solely your responsibility. We provide no warranties,
        guarantees, or insurance against such risks. Participation is for protocol utility purposes only, and you
        represent that you have no expectation of profits derived from the efforts of others. You acknowledge that this
        does not constitute money transmission or any financial service, and that bridged assets may lose all value if
        the PulseChain Bridge becomes inoperable.
      </p>

      <div style={styles.section}>2. Eligibility and Account Registration</div>

      <p style={styles.smallLead}>To enter into this rental Agreement and use the Services, you must:</p>

      <BulletList
        items={[
          '● Be at least 18 years old (or the age of majority in your jurisdiction, if higher).',
          '● Be capable of forming a legally binding contract.',
          '● Not be located in a jurisdiction where use of the Services is prohibited by law, including\nU.S.-sanctioned regions or areas with cryptocurrency restrictions.',
        ]}
      />

      <p style={styles.para}>
        During registration, you must provide accurate, current, and complete information, and you agree to update this
        information as necessary to maintain its accuracy. You are responsible for safeguarding your account
        credentials, including any passwords and access to rented private keys. We are not liable for any losses or
        damages resulting from unauthorized access to your account due to your failure to secure it. We may require
        identity verification for compliance with anti-money laundering (AML) regulations. By registering, you
        acknowledge and represent that your use is for utility purposes only, with no intent to transmit value or engage
        in speculative activities, and that you are not relying on Coast for any financial service. You further
        acknowledge that bridged assets on PulseChain are non-redeemable and may become valueless.
      </p>

      <div style={styles.section}>3. Rental Agreement Details</div>

      <p style={styles.para}>This section outlines the core terms of the software and private key rental.</p>

      <BulletList
        items={[
          '● Rental Grant: Subject to your compliance with these Terms and payment of rental fees,\nwe grant you a temporary rental license to use the software and private keys. This rental\nis non-exclusive, time-limited based on your selected subscription or usage plan (e.g.,\nhourly, daily, or monthly), and may be revoked at our sole discretion for any violation of\nthese Terms.',
          '● Permitted Use: You may use the rented private keys solely for your personal,\nnon-commercial execution of on-chain transactions, including swaps of bridged assets.\nYou must comply with all applicable laws, including but not limited to those governing\nblockchain activities, cryptocurrencies, securities, anti-money laundering (AML),\nknow-your-customer (KYC), and counter-terrorist financing (CTF) regulations.',
          '● No Transfer of Ownership or Custody: This rental does not transfer any ownership,\ntitle, custody, or permanent control of the private keys, software, or any underlying\nassets to you or from you to us. You remain fully responsible for any digital assets or\noutcomes associated with transactions you execute using the rented keys, including\nrisks related to bridged assets\' non-redeemability.',
          '● Rental Fees and Payment:',
        ]}
      />

      <BulletList
        indent={20}
        items={[
          '○ Rental fees will be clearly displayed in the software interface prior to initiating any\nrental period. Fees are calculated based on the duration and scope of the rental.',
          '○ All payments are non-refundable except as explicitly stated in these Terms or\nrequired by applicable law.',
          '○ Account Balances: Any balances in your account represent prepayments\nexclusively for future rentals of the software and private keys. These balances\nare not deposits in a financial sense, do not accrue interest, and are not\nconvertible to USD or any other fiat or digital currency outside of the Services.\nBalances can only be applied toward rental fees within the Coast platform.',
        ]}
      />

      <BulletList
        items={[
          '● Payment Processing: All deposits (prepayments) into your account and any\nwithdrawals (refunds of unused prepayments, if applicable) are handled exclusively by\nCTS Global (Montana), our subsidiary. These deposits and withdrawals represent\npayments solely for the software rental services and do not constitute financial\ntransactions, investments, or asset transfers. We do not directly handle, process, or\ncontrol these payments. You agree to abide by CTS Global\'s separate terms of service,\nprivacy policy, and any associated fees or requirements. CTS Global (Montana) is\nresponsible for the secure processing of all such transactions, and any disputes related\nto payments must be directed to them. You acknowledge that payments do not constitute\nstored value or transmission; they are solely advance licenses for software access, and\nany involvement with bridged assets does not change their non-redeemable nature.',
        ]}
      />

      <div style={styles.section}>4. User Obligations and Responsibilities</div>

      <p style={styles.smallLead}>As a renter under this Agreement, you agree to:</p>

      <BulletList
        items={[
          '● Legal Compliance: Use the Services only in full compliance with all applicable local,\nstate, federal, and international laws and regulations, including tax reporting and AML\nrequirements. You must not use the Services for any illegal or unauthorized purposes,\nincluding fraud, money laundering, terrorist financing, or any prohibited activities. You\nacknowledge and represent that your use does not involve money transmission and that\nyou assume all compliance obligations, including risks associated with bridged assets\'\npotential valuelessness.',
          '● Transaction Management: Initiate and confirm all on-chain transactions independently.\nWe offer no guarantees regarding transaction performance, including speed, gas fees,\nsuccess rates, or blockchain network availability. You are responsible for securing your\nwallet and verifying all details, including the non-redeemable status of bridged assets.',
          '● Risk Acceptance: Fully understand and accept all risks associated with renting and\nusing the software and private keys, including but not limited to technical failures,\nsecurity breaches, economic losses, and blockchain-specific risks like smart contract\nvulnerabilities or bridge failures leading to asset illiquidity.',
          '● Prohibited Conduct: You must not:',
        ]}
      />

      <BulletList
        indent={20}
        items={[
          '○ Attempt to reverse engineer, decompile, disassemble, modify, or create derivative\nworks from the Services or rented private keys.',
          '○ Use the Services to violate, infringe, or misappropriate any third-party intellectual\nproperty rights, privacy rights, or other legal rights.',
          '○ Share, rent, sublicense, or transfer the private keys or software access to any\nthird party without our prior written consent.',
          '○ Engage in any activity that could overload, damage, or impair the Services, such\nas denial-of-service attacks or excessive automated usage.',
          '○ Use the Services in connection with any high-risk activities, including but not\nlimited to gambling, speculative trading, or unregulated financial services, unless\nexplicitly permitted by law.',
        ]}
      />

      <div style={styles.section}>5. CST Knowledge Token</div>

      <p style={styles.para}>
        CST is a "Knowledge Token" issued within the Coast ecosystem. It is not a stablecoin, is not redeemable for fiat
        currency, and is not backed by any fiat currency or other assets. As such, CST does not qualify as a payment
        stablecoin under the GENIUS Act or similar regulatory frameworks. CST serves solely as a digital token
        representing knowledge or achievements earned through participation in optional features, such as the Game of
        Skill or Coast Lock & Earn (described in Sections 10 and 6). It has no monetary value outside of the Coast
        platform and cannot be exchanged, transferred, or redeemed for cash or other financial instruments. CST is used
        for protocol utility only, and any references to "rewards" or "inflation" are participation-based distributions
        without guarantees. You acknowledge that CST does not substitute for value and its distribution does not
        constitute transmission, and that interactions with bridged assets does not alter their non-redeemable status.
      </p>

      <div style={styles.section}>6. Coast Lock & Earn</div>

      <p style={styles.para}>
        Coast Lock & Earn is an optional feature that allows eligible users to voluntarily lock their CST Knowledge
        Tokens in smart contract vaults on the PulseChain blockchain for fixed periods to participate in protocol-level
        inflation distribution. This is a decentralized, time-based participation mechanism for ecosystem engagement and
        is not an investment product, financial service, or yield-generating vehicle. Participation requires your
        explicit acceptance of the separate Coast Lock & Earn Terms of Use, available at 0xcoast.com/terms-lock-earn,
        which provide full details on how it works, eligibility, risks, and disclosures and further disclaim any
        financial or transmission activities. By participating, you acknowledge that rewards, if any, arise solely from
        predetermined protocol inflation mechanics and are not tied to Coast Software's revenues or efforts. We reserve
        the right to modify or terminate this feature at any time. This feature does not involve acceptance or
        transmission of value, and any bridged assets are non-redeemable and may become valueless if the bridge fails.
      </p>

      <div style={styles.section}>7. Disclaimers and Limitations of Liability</div>

      <p style={styles.allCapsBlock}>
        THE SERVICES, SOFTWARE, PRIVATE KEYS, AND FEATURES LIKE COAST LOCK &{'\n'}
        EARN ARE PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS, WITHOUT ANY{'\n'}
        WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY,{'\n'}
        INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR{'\n'}A PARTICULAR PURPOSE, TITLE,
        NON-INFRINGEMENT, OR ACCURACY.
      </p>

      <p style={styles.smallLead}>We disclaim all liability for:</p>

      <BulletList
        items={[
          '● Any direct or indirect losses, damages, or expenses arising from your use of the rented\nsoftware, private keys, on-chain transactions, or features like Lock & Earn, including\nlosses from bridged asset illiquidity or bridge failure.',
          '● Inaccuracies, delays, or failures in blockchain data, price feeds, inflation funding, or\nthird-party integrations.',
          '● Any interruptions, errors, bugs, viruses, or downtime in the Services.',
        ]}
      />

      <p style={styles.allCapsBlock}>
        TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL WE, OUR{'\n'}
        AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR CTS GLOBAL{'\n'}
        (MONTANA) BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL,{'\n'}
        EXEMPLARY, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF{'\n'}
        PROFITS, DATA, GOODWILL, DIGITAL ASSETS, OR OTHER INTANGIBLE LOSSES, EVEN{'\n'}
        IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
      </p>

      <p style={styles.para}>
        Our total aggregate liability to you under this Agreement shall not exceed the total rental fees paid by you to
        us in the twelve (12) months preceding the event giving rise to the claim.
      </p>

      <div style={styles.section}>8. Indemnification</div>

      <p style={styles.para}>
        You agree to indemnify, defend, and hold harmless Coast Software (Nevada), our affiliates, officers, directors,
        employees, agents, and CTS Global (Montana) from and against any and all claims, demands, liabilities, damages,
        losses, costs, and expenses (including reasonable attorneys' fees) arising out of or related to: (i) your use of
        the Services; (ii) your violation of these Terms; (iii) your violation of any applicable law or regulation; or
        (iv) your infringement of any third-party rights, including any losses related to bridged assets or bridge
        failures.
      </p>

      <div style={styles.section}>9. Termination of Rental Agreement</div>

      <p style={styles.para}>
        We may terminate or suspend this rental Agreement and your access to the Services at any time, with or without
        cause or notice, including for suspected violations of these Terms, non-payment, or security concerns. Upon
        termination:
      </p>

      <BulletList
        items={[
          '● Your rental license immediately ends, and you must cease all use of the software and\nprivate keys.',
          '● Any remaining account balances may be forfeited or refunded at our discretion, subject\nto CTS Global\'s processing.',
          '● Sections of these Terms that by their nature should survive termination (e.g., disclaimers,\nindemnification, limitations of liability) will continue to apply.',
        ]}
      />

      <p style={styles.para}>
        You may terminate this Agreement by ceasing use of the Services and requesting account closure through our
        support channels.
      </p>

      <div style={styles.section}>10. Separate Agreement for Game of Skill (Play Feature)</div>

      <p style={styles.para}>
        The Services may offer optional participation in a "Game of Skill" (also referred to as the Play feature), a
        skill-based activity where you pay a non-refundable entry fee in USD (via Stripe) to answer multiple-choice
        questions and potentially earn CST Knowledge Tokens as rewards. This is not gambling or a lottery; outcomes
        depend on skill. Participation is governed by a separate agreement ("Game of Skill Agreement"), which you must
        explicitly accept before paying fees or playing. The agreement, available at 0xcoast.com/terms-game-skill,
        provides detailed rules, payments, gameplay, rewards distribution (manual via verified wallet), and risks (e.g.,
        delays, errors, tax obligations) and further disclaims any financial or transmission activities. We do not
        guarantee prizes or timings, and entry fees are non-refundable. This feature is entirely optional and separate
        from the software rental. This feature does not involve acceptance or transmission of value, and any bridged
        assets are non-redeemable.
      </p>

      <div style={styles.section}>11. Data and Privacy</div>

      <p style={styles.para}>
        We collect limited data such as wallet addresses, transaction details, session information (via cookies), and
        game attempts to operate the Services, process distributions, and comply with laws. On-chain data is public;
        off-chain data is stored securely (e.g., in PostgreSQL). We share data only as required (e.g., with Stripe,
        authorities for AML). By using the Services, you consent to these practices and acknowledge compliance with
        privacy laws.
      </p>

      <div style={styles.section}>12. Intellectual Property</div>

      <p style={styles.para}>
        The Services, including software, designs, and content, are owned by us or licensors and protected by IP laws.
        You receive a limited license for personal use only.
      </p>

      <div style={styles.section}>13. Governing Law and Dispute Resolution</div>

      <p style={styles.para}>
        These Terms and this rental Agreement are governed by the laws of the State of Montana, USA, without regard to
        its conflict of laws principles. Any disputes arising out of or relating to these Terms shall be resolved
        exclusively through binding arbitration in Montana, administered in accordance with the rules of the American
        Arbitration Association. The arbitration shall be conducted by a single arbitrator, and judgment on the award
        may be entered in any court of competent jurisdiction. You waive any right to participate in class actions,
        class arbitrations, or representative actions.
      </p>

      <div style={styles.section}>14. Miscellaneous Provisions</div>

      <BulletList
        items={[
          '● Severability: If any provision of these Terms is held to be invalid, illegal, or\nenforceable, the remaining provisions shall remain in full force and effect.',
          '● Entire Agreement: These Terms constitute the entire agreement between you and us\nregarding the rental of the Services, superseding any prior agreements, understandings,\nor representations.',
          '● Waiver: No waiver of any breach or default under these Terms shall be deemed a waiver\nof any subsequent breach or default.',
          '● Assignment: We may assign these Terms or any rights hereunder without your consent.\nYou may not assign these Terms without our prior written consent.',
          '● Force Majeure: We shall not be liable for any delay or failure to perform due to causes\nbeyond our reasonable control, including acts of God, war, terrorism, blockchain failures,\nor network outages, including PulseChain Bridge failures.',
          '● Third-Party Services: Integrations (e.g., Stripe, PulseChain) are subject to their terms;\nwe are not responsible.',
          '● Regulatory Compliance: Coast does not engage in money transmission; per\nFIN-2019-G001, exemptions apply for non-custodial tools, unhosted wallets, and\nsoftware providers where users retain total independent control over private keys and\nvalue, with no acceptance or transmission by Coast. We comply with U.S. laws (e.g.,\nFinCEN); you must provide info for verification if required. Restricted jurisdictions are\nprohibited. You acknowledge that your use does not involve money transmission and\nassume all compliance obligations, including risks from bridged assets\'\nnon-redeemability or bridge failure.',
          '● Contact Information: For questions about these Terms or the Services, please contact\nus at legal@0xcoast.com.',
        ]}
      />

      <p style={styles.para}>
        By using the Services, you confirm that you have read, understood, and agree to be bound by this rental
        Agreement in its entirety.
      </p>

      <hr style={styles.divider} />
    </div>
  )
}
